import Plotly, { Layout } from "plotly.js-dist-min";
import { Temporal } from "@js-temporal/polyfill";
import { getColour, PlotableData } from "@/helpers/helpers";
import { ConditionMatchedPeriod } from "@/interfaces/ConditionMatchedPeriod";

const plot = async (sensorData: PlotableData[]) => {
  const layout = {
    autosize: true,
    width: window.innerWidth * 0.9,
    height: window.innerHeight * 0.7,
    xaxis: {
      title: {
        text: "Time",
      },
      automargin: true,
    },
    yaxis: {
      title: {
        text: "Acceleration (g)",
      },
    },
  } as Partial<Layout>;

  const dataToPlot = sensorData.map(async ({ SensorName, data }) => {
    // Make an Instant (date) object from the start time string. Convert this into a zoned date time object
    // with the user's chosen timezone. Strip out the timezone, and put in the browser's timezone as this
    // is what plotly (and the Date object) uses.
    return [
      {
        x: data.X.x,
        y: data.X.y,
        type: "scatter",
        name: `${SensorName} X`,
        line: { color: await getColour(`${SensorName} X`) },
      },
      {
        x: data.Y.x,
        y: data.Y.y,
        type: "scatter",
        name: `${SensorName} Y`,
        line: { color: await getColour(`${SensorName} Y`) },
      },
      {
        x: data.Z.x,
        y: data.Z.y,
        type: "scatter",
        name: `${SensorName} Z`,
        line: { color: await getColour(`${SensorName} Z`) },
      },
    ];
  });

  const withoutPromises = await Promise.all(dataToPlot);

  const reduced = withoutPromises.flat();

  const data = reduced as Plotly.Data[];
  const graph = document.getElementById("graph");

  if (graph == null) {
    return;
  }

  Plotly.newPlot(graph, data, layout);
};

// One sensor's (node's) condition-matched PSD windows, ready to plot.
type ConditionMatchedPsdSeries = {
  label: string;
  periods: ConditionMatchedPeriod[];
};

const plotConditionMatchedPsd = async (
  seriesBySensor: ConditionMatchedPsdSeries[],
  scale: "log" | "loglinear" = "log",
  elementID: string
) => {
  // "log" plots raw PSD on a Plotly log axis; "loglinear" plots log10(PSD) on a linear axis.
  // PSD values are non-negative - drop any <= 0 (log undefined) so Plotly leaves a gap there.
  const transformY = (value: number[]): (number | null)[] =>
    scale === "loglinear" ? value.map((v) => (v > 0 ? Math.log10(v) : null)) : value;

  // One trace per (node, window). Colour is hashed off the trace name, same as the raw
  // acceleration plot above - each trace just gets its own stable colour.
  const dataToPlot = seriesBySensor.map(async ({ label, periods }) => {
    const traces = await Promise.all(
      periods.map(async ({ start_time, frequency, value }) => {
        // Truncate to whole seconds - the sub-second offset is just noise in the legend.
        const startLabel = Temporal.Instant.from(start_time).toString({ smallestUnit: "second" });
        const name = `${label} @ ${startLabel}`;
        return {
          x: frequency,
          y: transformY(value),
          type: "scatter",
          name,
          line: { color: await getColour(name) },
        };
      })
    );
    return traces;
  });

  const withoutPromises = await Promise.all(dataToPlot);

  const reduced = withoutPromises.flat();

  const data = reduced as Plotly.Data[];

  const layout: Partial<Layout> = {
    autosize: true,
    xaxis: {
      title: "Frequency (Hz)",
      type: "linear",
      autorange: true,
    },

    yaxis: {
      title: scale === "loglinear" ? "log₁₀ Power Spectral Density (g²/Hz)" : "Power Spectral Density (g²/Hz)",
      type: scale === "loglinear" ? "linear" : "log",
      autorange: true,
    },
  };

  const graph = document.getElementById(elementID);

  if (graph == null) {
    return;
  }
  Plotly.newPlot(graph, data, layout);
};

export { plotConditionMatchedPsd, plot };
